// assets/src/api/calendar.js

import { refreshNonce } from './nonce';
import { postNaluAjax } from './ajax';

// == Utilities ===============================================================

const ONE_MINUTE = 60_000;

/**
 * Single-flight + debounce + short-ttl cache for any async function.
 * - Collapses identical concurrent calls (same key) into ONE in-flight request.
 * - Debounces rapid calls; only the last call within waitMs executes.
 * - Aborts the previous in-flight request when a *different* key arrives.
 * - Short TTL memory cache to avoid back-to-back identical fetches.
 *
 * @param {(args:any)=>string} makeKey - serialize args -> stable key
 * @param {(args:any, signal?:AbortSignal)=>Promise<any>} runner - actual fetcher
 * @param {number} waitMs - debounce window
 * @param {number} ttlMs  - memory cache TTL for identical key
 */
function makeCoalescer(makeKey, runner, waitMs = 300, ttlMs = 5_000) {
  let timer = null;
  let lastArgs = null;
  let lastKey  = null;

  let inFlight = null;       // { key, promise }
  let controller = null;     // AbortController for in-flight

  const cache = new Map();   // key -> { at:number, data:any }
  const waiters = [];        // queued resolvers for debounced callers

  function resolveAllOk(data) {
    while (waiters.length) waiters.shift().resolve(data);
  }
  function resolveAllErr(err) {
    while (waiters.length) waiters.shift().reject(err);
  }

  return function coalescedCall(args) {
    return new Promise((resolve, reject) => {
      // Remember this caller to resolve when the debounced job runs
      waiters.push({ resolve, reject });

      lastArgs = args;
      lastKey  = makeKey(args);

      // If we have fresh cache for the current key, serve immediately.
      const hit = cache.get(lastKey);
      if (hit && (Date.now() - hit.at) < ttlMs) {
        clearTimeout(timer);
        // resolve everyone with cached data
        resolveAllOk(hit.data);
        return;
      }

      // If there is an identical in-flight request, piggyback on it.
      if (inFlight && inFlight.key === lastKey) {
        // do nothing; we'll resolveAll… when it completes
        return;
      }

      // Debounce: reset the timer
      clearTimeout(timer);
      timer = setTimeout(async () => {
        // New key vs in-flight? abort the older request
        if (inFlight && inFlight.key !== lastKey && controller) {
          controller.abort('superseded');
        }

        controller = new AbortController();
        const signal = controller.signal;

        // Kick request
        const thisKey = lastKey;
        const thisArgs = lastArgs;

        const p = runner(thisArgs, signal)
          .then((data) => {
            // cache the success
            cache.set(thisKey, { at: Date.now(), data });
            if (inFlight && inFlight.key === thisKey) inFlight = null;
            // Deliver to the shared waiters ONLY when this request is still the
            // latest key. `waiters` is one queue per lane and resolveAll* drains
            // ALL of them, so a superseded (stale) request completing here would
            // otherwise hand the newer caller the wrong key's data — e.g. a
            // children=N load returning the previous low-children blocked set,
            // making the date picker show availability it shouldn't. The newest
            // key always gets its own runner (timer / piggyback / cache hit),
            // which is what delivers to every waiter — "latest query wins."
            if (thisKey === lastKey) resolveAllOk(data);
            return data;
          })
          .catch((err) => {
            if (inFlight && inFlight.key === thisKey) inFlight = null;
            // Same guard: a superseded request that was aborted ('superseded')
            // must NOT reject the shared waiters, or it would fail the *newer*
            // caller's promise with this stale request's AbortError (the newer
            // runner that aborted it is already in flight and will deliver).
            if (thisKey === lastKey) resolveAllErr(err);
            throw err;
          });

        inFlight = { key: thisKey, promise: p };
        try { await p; } catch (_) {}
      }, waitMs);
    });
  };
}

// == Low-level XHR helpers ===================================================

// Swap the stale nonce in a param bag for a fresh one on retry. GET params use
// `nonce`, POST bodies use `security` — replace whichever is present.
function withFreshNonce(params, fresh) {
  const next = { ...params };
  if ('nonce' in next) next.nonce = fresh;
  if ('security' in next) next.security = fresh;
  return next;
}

function wpAjaxGet(params, { signal } = {}) {
  const doFetch = (p) => {
    const usp = new URLSearchParams(p);
    return fetch(`${naluBookingVars.ajax_url}?${usp}`, {
      method: 'GET',
      credentials: 'same-origin',
      signal
    }).then(async (res) => ({ res, json: await res.json().catch(() => null) }));
  };

  const finalize = (json) => {
    if (!json || !json.success) {
      const msg = json?.data?.message || (typeof json?.data === 'string' ? json.data : null) || 'AJAX request failed';
      throw new Error(msg);
    }
    return json.data;
  };

  return doFetch(params).then(({ res, json }) => {
    // Stale-page nonce → refresh once and replay with the fresh value.
    if (res.status === 403) {
      return refreshNonce().then((fresh) => {
        if (!fresh) return finalize(json);
        return doFetch(withFreshNonce(params, fresh)).then(({ json: j2 }) => finalize(j2));
      });
    }
    return finalize(json);
  });
}

// POST calendar lookups (no abort signal in practice — only the GET coalescer
// aborts) share the common nonce-healing POST helper.
function wpAjaxPost(formData) {
  return postNaluAjax(formData);
}

// == Existing 14-day endpoints (unchanged semantics) =========================

export async function fetchCalendarRange({ start_date, end_date, adults, children, session_start = false, intent = '' }) {
  const body = new FormData();
  body.append('action', 'nalu_get_calendar');
  body.append('security', naluBookingVars.security);
  body.append('start_date', start_date);
  body.append('end_date', end_date);
  body.append('adults', adults);
  body.append('children', children);
  // One-shot per visit: marks the first calendar load so the portal can count
  // it as wp.booking.started (per-shopper denominator). See BookingForm.js.
  if (session_start) body.append('session_start', '1');
  // Deep-link intent (e.g. "sunset"); portal returns intent-ordered tours.
  // Omitted when empty so the request is identical to the pre-intent shape.
  if (intent) body.append('intent', intent);
  return wpAjaxPost(body);
}

export async function fetchCalendar({ date, adults, children }) {
  const body = new FormData();
  body.append('action', 'nalu_get_calendar_by_date');
  body.append('date', date);
  body.append('adults', adults);
  body.append('children', children);
  return wpAjaxPost(body);
}

// == Simple Calendar (GET) with single-flight, debounce, abort, cache ========

function keyForSimple({ start, end, adults, children, tids = '' }) {
  // NB: tids is comma-separated list; keep as provided to preserve key stability
  return `s=${start}|e=${end}|a=${adults}|c=${children}|t=${tids}`;
}

// Runner for multi-tour (preferred)
async function runSimpleCalendarMulti({ start, end, adults, children, tids }, signal) {
  return wpAjaxGet({
    action: 'nalu_get_simple_calendar',
    nonce: naluBookingVars.security,
    start, end, adults, children,
    tids,
    probe_today: 1
  }, { signal });
}

// Runner for single tourId (legacy)
async function runSimpleCalendarSingle({ start, end, adults, children, tourId }, signal) {
  return wpAjaxGet({
    action: 'nalu_get_simple_calendar',
    nonce: naluBookingVars.security,
    start, end, adults, children,
    tourId: tourId || 0,
    probe_today: 1
  }, { signal });
}

// Debounce window: 300ms (feels instant but collapses click-spam).
// Cache TTL: 5s (prevents triple fetch on mount and micro re-renders).
const callSimpleMulti   = makeCoalescer(keyForSimple, runSimpleCalendarMulti, 300, 5000);
const callSimpleSingle  = makeCoalescer(keyForSimple, runSimpleCalendarSingle, 300, 5000);

// Separate lanes for opportunistic prefetch. A coalescer is single-flight and
// aborts the in-flight request whenever a different key arrives — correct for
// "latest interactive query wins," but fatal if a background prefetch shares
// the lane: it would cancel the foreground visible-month load the user is
// waiting on. Keeping prefetch on its own coalescer lets the two run
// concurrently without cross-aborting. (Per-month results are deduped upstream
// via BookingForm._blockedByMonth, so the split caches don't cause refetches.)
const callSimpleMultiPrefetch  = makeCoalescer(keyForSimple, runSimpleCalendarMulti, 300, 5000);
const callSimpleSinglePrefetch = makeCoalescer(keyForSimple, runSimpleCalendarSingle, 300, 5000);

/**
 * Public API – keep the same signatures you already use.
 */
export function fetchSimpleCalendar(start, end, tourId, adults, children) {
  return callSimpleSingle({ start, end, adults, children, tourId });
}

export function fetchSimpleCalendarMulti(start, end, tidsArray, adults, children) {
  const tids = Array.isArray(tidsArray) ? tidsArray.join(',') : (tidsArray || '');
  return callSimpleMulti({ start, end, adults, children, tids });
}

// Prefetch variants — same request, isolated lane so they never abort a
// foreground load. Use only for fire-and-forget warming.
export function fetchSimpleCalendarPrefetch(start, end, tourId, adults, children) {
  return callSimpleSinglePrefetch({ start, end, adults, children, tourId });
}

export function fetchSimpleCalendarMultiPrefetch(start, end, tidsArray, adults, children) {
  const tids = Array.isArray(tidsArray) ? tidsArray.join(',') : (tidsArray || '');
  return callSimpleMultiPrefetch({ start, end, adults, children, tids });
}
